/* eslint-disable camelcase */
// --- Request Processors

import { globalState, parseJSON } from '@/components/helpers/utils'
import { getProductPrices } from '../../storefront/products'

/**
 *
 * @param {Facet[]} facets
 * @returns {NormalisedFacet[]}
 */
export function normaliseFacets(facets) {
  return facets.map((facet) => {
    return {
      ...facet,
      values: facet.values.filter((item) => item.value !== '"')
    }
  })
}

/**
 *
 * Normalise the results from the searchspring API
 * @param {Sorting[]} options
 * @returns {NormalisedSorting[]}
 */
export function normaliseSortOptions(options) {
  return options.map((option) => {
    return {
      ...option,
      active: option.active ?? 0
    }
  })
}

/**
 *
 * Normalise the results from the searchspring API
 * @param {Result} result
 * @returns {Swatch[]}
 */
export function normaliseSwatches(result) {
  const parsedSwatches = parseJSON(result?.mfield_dotapparel21_siblings, 'array')
  if (!parsedSwatches) return []
  const publishedSwatches = parsedSwatches.filter((swatch) => swatch.published)

  const swatchesWithImages = publishedSwatches.map((swatch) => {
    return {
      ...swatch,
      url: result.url
    }
  })
  return swatchesWithImages.sort((swatchA, swatchB) => {
    if (swatchA.isCurrentProduct === swatchB.isCurrentProduct) return 0
    if (swatchA.isCurrentProduct) return -1
    return 1
  })
}

/**
 * Normalise delimited string/array types into arrays
 * @param {String|Array} result
 * @returns {Array}
 */
export function normalisedArray(result) {
  if (result?.includes('|')) {
    return result?.split('|')
  } else if (typeof result === 'string') {
    return [result]
  } else {
    return result
  }
}

/**
 *
 * Normalise the results from the searchspring API
 * @param {Result} result
 * @returns {NormalisedImage[]}
 */
export function normaliseImages(result) {
  const { images, images_alt } = result
  const srcs = normalisedArray(images)
  const alts = normalisedArray(images_alt)

  return srcs?.map((src, index) => {
    return {
      src,
      alt: alts ? alts[index] : ''
    }
  })
}

/**
 *
 * Normalise the results from the searchspring API
 * @param {Result[]} results
 * @returns {Promise[NormalisedResult[]]}
 */
export async function normaliseResults(results) {
  if (!results) return []
  const normalisedResults = results?.map((result) => {
    const swatches = normaliseSwatches(result)
    const images = normaliseImages(result)
    const variants = parseJSON(result.variants, 'array')
    return {
      ...result,
      images,
      swatches,
      variants
    }
  })

  if (!normalisedResults || !normalisedResults.length) return []

  if (globalState.localization.country !== globalState.shopify.currency) {
    return await localiseResults(normalisedResults)
  }
  return normalisedResults
}

async function localiseResults(normalisedResults) {
  const localisedPrices = await getProductPrices(normalisedResults)
  const localisedResults = normalisedResults.map((result) => {
    const priceMatch = localisedPrices?.find((product) => {
      return product.id.split('/').pop() === result.uid
    })

    if (priceMatch) {
      const resultVariants = result.variants.map((variant) => {
        return {
          ...variant,
          price: priceMatch.price
        }
      })
      return {
        ...result,
        variants: resultVariants,
        price: priceMatch.price
      }
    } else {
      return result
    }
  })
  return localisedResults
}

/**
 * Normalise blog article results from the SearchSpring API
 * @param {Result[]} results
 * @returns {NormalisedBlogResult[]}
 */
export async function normaliseBlogResults(results) {
  if (!results) return []
  return results.map((result) => ({
    id: result.uid,
    title: result.name,
    url: result.url,
    excerpt: result.description,
    image: result.thumbnailImageUrl ? { src: result.thumbnailImageUrl, alt: result.name } : null,
    published_at: result.published_at,
    author: result.author,
    tags: result.tags,
    blog_handle: result.blog_handle
  }))
}

/**
 *
 * @param {Result[]} results
 * @returns {NormalisedResult[]}
 */
export async function normaliseRecommendations(results) {
  const normalisedResults = results?.results?.map((result) => {
    const swatches = normaliseSwatches(result.attributes)
    const variants = parseJSON(result.attributes.variants, 'array')
    const images = normaliseImages(result.attributes)

    return {
      ...result,
      ...result.attributes,
      ...result.mappings.core,
      images,
      variants,
      swatches
    }
  })

  if (!normalisedResults || !normalisedResults.length) return []

  if (globalState.localization.country !== globalState.shopify.currency) {
    return await localiseResults(normalisedResults)
  }

  return normalisedResults
}
